import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { EV_CONNECTOR_TYPES } from "../masterdata/assets.js";
import { evConnectorStatusSchema, evExportFieldKeySchema, evIdentificationSchema, evLimitReasonSchema, evLoadMethodSchema, evOperationModelSchema, evSessionPhaseSchema, } from "./enums.js";
/**
 * Şarj İŞLETİMİ (Prompt 19, ADR 0019 §11–12, #74, #76): yük yönetimi görünümü, canlı pano, şarj ağı işletmecisinden içe
 * aktarım (sahte adaptör) ve düzenleyici dışa aktarım profilleri. İzinler: okuma `ev.read`, içe aktarım `ev.operate`,
 * profil yazımı `ev.manage`, dışa aktarım `ev.report.read`; bayrak `EV`.
 */
// ── Yük yönetimi ─────────────────────────────────────────────────────────────
export const evStationLoadQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(500).default(100).describe("Sınır tarihçesinde en çok satır"),
});
export const evStationLoadSchema = z.object({
    stationId: uuidSchema,
    operationModel: evOperationModelSchema,
    loadMethod: evLoadMethodSchema,
    maxGridKw: z.string().nullable(),
    safetyMarginPercent: z.string(),
    minSessionPowerKw: z.string(),
    priorityFleet: z.number().int(),
    prioritySubscription: z.number().int(),
    priorityApp: z.number().int(),
    availableW: z
        .number()
        .int()
        .nullable()
        .describe("Paylaştırılabilir güç = azami güç × (1 − emniyet payı); yük yönetimi kapalıysa / güç yoksa null"),
    drawingW: z.number().int().describe("Canlı oturumların son ölçülen güç toplamı"),
    liveSessions: z.array(z.object({
        sessionId: uuidSchema,
        chargerId: uuidSchema,
        chargerName: z.string(),
        connectorNo: z.number().int(),
        connectorPowerKw: z.string().nullable(),
        phase: evSessionPhaseSchema,
        identification: evIdentificationSchema,
        startedAt: timestampSchema,
        energyKwh: z.string(),
        powerW: z.number().int().nullable(),
        currentLimitW: z.number().int().nullable(),
        suspended: z.boolean(),
        limitAt: timestampSchema.nullable(),
    })),
    history: z.array(z.object({
        id: uuidSchema,
        at: timestampSchema,
        sessionId: uuidSchema,
        chargerId: uuidSchema,
        chargerName: z.string(),
        connectorNo: z.number().int(),
        method: z.enum(["EQUAL", "PRIORITY"]),
        availableW: z.number().int(),
        previousLimitW: z.number().int().nullable(),
        limitW: z.number().int(),
        suspended: z.boolean(),
        reason: evLimitReasonSchema,
        commandId: uuidSchema.nullable(),
        commandStatus: z.string().nullable(),
    })),
});
// ── Canlı pano ───────────────────────────────────────────────────────────────
export const evOverviewQuerySchema = z.object({ stationId: uuidSchema });
export const evOverviewConnectorSchema = z.object({
    chargerId: uuidSchema,
    chargerName: z.string(),
    connectorNo: z.number().int(),
    connectorType: z.enum(EV_CONNECTOR_TYPES),
    powerKw: z.string(),
    online: z.boolean(),
    status: evConnectorStatusSchema.nullable().describe("Son bilinen OCPP durumu; hiç bildirilmediyse null"),
    errorCode: z.string().nullable(),
    statusSince: timestampSchema.nullable(),
    faulted: z.boolean(),
    session: z
        .object({
        sessionId: uuidSchema,
        phase: evSessionPhaseSchema,
        identification: evIdentificationSchema,
        startedAt: timestampSchema,
        energyKwh: z.string(),
        powerW: z.number().int().nullable(),
        socPercent: z.string().nullable(),
        durationSeconds: z.number().int(),
        currentLimitW: z.number().int().nullable(),
    })
        .nullable(),
});
export const evOverviewSchema = z.object({
    stationId: uuidSchema,
    stationName: z.string(),
    operationModel: evOperationModelSchema,
    generatedAt: timestampSchema,
    connectors: z.array(evOverviewConnectorSchema),
    today: z.object({
        date: localDateSchema,
        sessionCount: z.number().int().describe("Bugün başlayan oturumlar"),
        completedCount: z.number().int(),
        liveCount: z.number().int(),
        energyKwh: z.string(),
        totalAmount: moneySchema.describe("Bugün başlayan TAMAMLANMIŞ oturumların tutarı (KDV dahil)"),
        unpaidCount: z.number().int().describe("Tamamlanmış, ödenmemiş oturumlar (tüm günler)"),
        unpaidAmount: moneySchema,
        faultedConnectors: z.number().int(),
        offlineChargers: z.number().int(),
    }),
});
// ── Şarj ağı işletmecisi (ortak) içe aktarımı ────────────────────────────────
export const createEvPartnerImportSchema = z
    .object({
    from: timestampSchema,
    to: timestampSchema,
})
    .superRefine((value, context) => {
    const from = Date.parse(value.from);
    const to = Date.parse(value.to);
    if (to <= from)
        context.addIssue({ code: "custom", path: ["to"], message: "Bitiş başlangıçtan sonra olmalı" });
    if (to - from > 31 * 86_400_000)
        context.addIssue({ code: "custom", path: ["to"], message: "Aralık en çok 31 gün olabilir" });
});
export const evPartnerImportSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    partnerCode: z.string(),
    periodFrom: timestampSchema,
    periodTo: timestampSchema,
    status: z.enum(["COMPLETED", "FAILED"]),
    importedCount: z.number().int(),
    skippedCount: z
        .number()
        .int()
        .describe("Daha önce içe aktarılmış (ortak oturum kimliğiyle tekil) oturumlar"),
    totalEnergyKwh: z.string(),
    totalAmount: moneySchema,
    error: z.string().nullable(),
    warnings: z.array(z.string()),
    createdBy: uuidSchema.nullable(),
    createdAt: timestampSchema,
});
export const evPartnerImportPageSchema = pageOf(evPartnerImportSchema);
export const listEvPartnerImportsQuerySchema = pageQuerySchema.extend({ stationId: uuidSchema.optional() });
// ── Düzenleyici dışa aktarım (#74) ───────────────────────────────────────────
/** Varsayılan Türkçe sütun başlıkları (profil kendi başlığını yazabilir). */
export const EV_EXPORT_FIELD_LABELS = {
    sessionId: "Oturum no",
    stationCode: "İstasyon kodu",
    stationName: "İstasyon",
    chargerName: "Şarj ünitesi",
    ocppIdentity: "OCPP kimliği",
    connectorNo: "Konnektör",
    connectorType: "Konnektör türü",
    powerKw: "Konnektör gücü (kW)",
    currentType: "Akım türü",
    startedAt: "Başlangıç",
    chargingEndedAt: "Şarj bitişi",
    completedAt: "Tamamlanma",
    energyKwh: "Enerji (kWh)",
    chargingMinutes: "Şarj süresi (dk)",
    idleMinutes: "Bekleme (dk)",
    energyAmount: "Enerji tutarı (₺)",
    timeAmount: "Süre tutarı (₺)",
    idleAmount: "Bekleme tutarı (₺)",
    totalAmount: "Toplam tutar (₺)",
    paymentStatus: "Ödeme durumu",
    paymentMethod: "Ödeme yolu",
    identification: "Tanımlama",
    source: "Kaynak",
};
export const EV_EXPORT_FORMATS = ["CSV", "JSON"];
export const EV_EXPORT_DELIMITERS = [";", ",", "|", "\t"];
export const evExportFieldSchema = z.object({
    key: evExportFieldKeySchema,
    label: z.string().trim().min(1).max(60),
});
const exportProfileFields = z.object({
    code: z
        .string()
        .trim()
        .regex(/^[A-Z0-9][A-Z0-9_-]{1,39}$/, "Kod büyük harf / rakam / _ - (2–40)"),
    name: z.string().trim().min(1).max(120),
    format: z.enum(EV_EXPORT_FORMATS).default("CSV"),
    fields: z
        .array(evExportFieldSchema)
        .min(1, "En az bir alan seçilmelidir")
        .max(60)
        .refine((fields) => new Set(fields.map((field) => field.key)).size === fields.length, "Aynı alan iki kez seçilemez"),
    csvDelimiter: z.enum(EV_EXPORT_DELIMITERS).default(";"),
});
export const createEvExportProfileSchema = exportProfileFields;
/** PUT — profil TAMAMEN yazılır (kod değişmez). */
export const saveEvExportProfileSchema = exportProfileFields
    .omit({ code: true })
    .extend({ isActive: z.boolean().default(true), version: z.number().int().positive() });
export const evExportProfileSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    name: z.string(),
    format: z.enum(EV_EXPORT_FORMATS),
    fields: z.array(evExportFieldSchema),
    csvDelimiter: z.enum(EV_EXPORT_DELIMITERS),
    isActive: z.boolean(),
    version: z.number().int(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const evExportProfileListSchema = z.object({
    items: z.array(evExportProfileSchema),
    canEdit: z
        .boolean()
        .describe("Çağıran profil oluşturup düzenleyebilir mi: KİRACI kapsamlı `ev.manage` (istasyon kapsamı kısıtsız)"),
    availableFields: z
        .array(z.object({ key: evExportFieldKeySchema, defaultLabel: z.string() }))
        .describe("Seçilebilecek alanlar (kapalı liste, kişisel veri YOK)"),
});
export const evSessionExportQuerySchema = z
    .object({
    profileId: uuidSchema,
    from: localDateSchema.describe("Oturumun başladığı gün (İstanbul, dahil)"),
    to: localDateSchema,
    stationId: uuidSchema.optional(),
})
    .superRefine((value, context) => {
    if (value.from > value.to)
        context.addIssue({ code: "custom", path: ["to"], message: "Bitiş başlangıçtan önce olamaz" });
    if ((Date.parse(value.to) - Date.parse(value.from)) / 86_400_000 > 366)
        context.addIssue({ code: "custom", path: ["to"], message: "Aralık en çok 366 gün olabilir" });
});
/** JSON biçimi (CSV'de aynı sütunlar, UTF-8 BOM'lu, profilin ayracıyla). Yalnızca TAMAMLANMIŞ oturumlar. */
export const evSessionExportSchema = z.object({
    profile: z.object({ id: uuidSchema, code: z.string(), name: z.string() }),
    from: localDateSchema,
    to: localDateSchema,
    generatedAt: timestampSchema,
    fields: z.array(evExportFieldSchema),
    rows: z.array(z.record(z.string(), z.union([z.string(), z.number(), z.null()]))),
});
//# sourceMappingURL=operations.js.map